import { checkSegment, type DevEmail, type DevPhone, normalizePhone } from '@eventping/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Mail, Send, Smartphone } from 'lucide-react'
import { type FormEvent, Fragment, useState } from 'react'
import { toast } from 'sonner'
import { Card, Page, timeOf, Wordmark } from '@/components/shell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, useLive, useMe } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Development only (console SMS provider): every number the app has texted, as a phone
 * would show it, with a box to text back as that phone. The texts' links open the contact
 * page exactly as a real tap would.
 */
export function DevPhonesPage() {
  const me = useMe()
  const enabled = Boolean(me.data?.devTools)
  const phones = useQuery({
    queryKey: ['dev-phones'],
    queryFn: () => api<DevPhone[]>('/api/dev/phones'),
    enabled,
  })
  const emails = useQuery({
    queryKey: ['dev-emails'],
    queryFn: () => api<DevEmail[]>('/api/dev/emails'),
    enabled,
  })
  useLive(enabled ? '/api/dev/stream' : null, ['dev-phones'])
  useLive(enabled ? '/api/dev/stream' : null, ['dev-emails'])
  const [other, setOther] = useState('')

  if (me.data && !me.data.devTools) {
    return (
      <Page header={<Wordmark />}>The phone simulator is only available in local development.</Page>
    )
  }

  return (
    <Page
      header={
        <>
          <Wordmark />
          <span className="rounded-full bg-waiting-soft px-2.5 py-0.5 text-sm text-waiting">
            Dev
          </span>
        </>
      }
      className="max-w-6xl"
    >
      <Link
        to="/"
        className="mb-3 inline-flex min-h-10 items-center gap-1 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Back
      </Link>
      <h1 className="text-2xl font-semibold">Simulated phones</h1>
      <p className="mb-5 text-muted-foreground">
        Nothing is really sent. Each card is one number: texts from the app on the left, replies you
        simulate on the right. Try STOP, START, HELP, LINK, or “#CODE message”. Numbers appear here
        once they sign up on a join page (or are added).
      </p>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {phones.data?.map((p) => (
          <PhoneCard key={p.phone} phone={p} />
        ))}
        <Card>
          <h2 className="mb-2 font-semibold">Another number</h2>
          <p className="mb-3 text-sm text-muted-foreground">
            Text the app from a number it doesn’t know.
          </p>
          <Input
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="(555) 555-0199"
            type="tel"
            aria-label="Phone number"
          />
          {normalizePhone(other) && (
            <div className="mt-3">
              <Composer phone={normalizePhone(other) ?? ''} />
            </div>
          )}
        </Card>
      </div>
      {phones.data?.length === 0 && (
        <p className="mt-4 text-muted-foreground">
          Sign someone up with a number to see their phone here.
        </p>
      )}

      <h2 className="mt-10 mb-3 flex items-center gap-2 text-xl font-semibold">
        <Mail className="size-5" aria-hidden /> Email outbox
      </h2>
      {emails.data?.length === 0 && <p className="text-muted-foreground">No emails yet.</p>}
      <ul className="grid gap-3 md:grid-cols-2">
        {emails.data?.map((m) => (
          <li key={m.id} data-testid={`email-${m.to}`}>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">
                To {m.to} · {timeOf(m.createdAt)} · {m.kind}
              </p>
              <p className="mt-1 font-medium">{m.subject}</p>
              <p className="mt-2 text-sm whitespace-pre-wrap">
                <Linkified text={m.body} />
              </p>
            </Card>
          </li>
        ))}
      </ul>
    </Page>
  )
}

const URL_RE = /(https?:\/\/[^\s]+)/g

function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE)
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one fixed string, never reordered
          <a key={i} href={part} className="underline underline-offset-2 break-all">
            {part}
          </a>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one fixed string, never reordered
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  )
}

function PhoneCard({ phone }: { phone: DevPhone }) {
  return (
    <Card className="flex flex-col p-0" data-testid={`phone-${phone.phone}`}>
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <Smartphone className="size-5 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{phone.names.join(', ') || 'Unknown'}</p>
          <p className="text-sm text-muted-foreground">{phone.display}</p>
        </div>
        {phone.stopped && (
          <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-sm text-destructive">
            Stopped
          </span>
        )}
      </div>
      <ol className="max-h-[28rem] flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {phone.messages.map((m) => {
          const fromApp = m.direction === 'out'
          const segment = fromApp ? checkSegment(m.body) : null
          return (
            <li key={m.id} className={cn('flex flex-col', fromApp ? 'items-start' : 'items-end')}>
              <p
                className={cn(
                  'max-w-[90%] rounded-2xl px-3.5 py-2 text-[15px] whitespace-pre-wrap',
                  fromApp
                    ? 'rounded-bl-md bg-secondary'
                    : 'rounded-br-md bg-primary text-primary-foreground',
                )}
                data-direction={m.direction}
              >
                <Linkified text={m.body} />
              </p>
              <span className="mt-0.5 px-1 text-xs text-muted-foreground">
                {timeOf(m.createdAt)}
                {segment && ` · ${segment.septets}/160`}
                {m.status === 'failed' && ' · failed'}
              </span>
            </li>
          )
        })}
      </ol>
      <div className="border-t p-3">
        <Composer phone={phone.phone} />
      </div>
    </Card>
  )
}

function Composer({ phone }: { phone: string }) {
  const [body, setBody] = useState('')
  async function send(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    try {
      await api('/api/dev/inbound', { body: { from: phone, body } })
      setBody('')
    } catch (err) {
      toast.error((err as Error).message)
    }
  }
  return (
    <form onSubmit={send} className="flex gap-2">
      <Input
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Simulate a text (STOP, HELP, LINK...)"
        aria-label={`Simulate a text from ${phone}`}
      />
      <Button type="submit" size="icon" aria-label="Send simulated text" disabled={!body.trim()}>
        <Send />
      </Button>
    </form>
  )
}
